/**
 * CotacoesBloco — bloco "Terceiros e serviços" do cockpit (CotacaoTerceiro).
 *
 * Dois estados persistidos (Q1): ESTIMADA (warning) → FIRME (success);
 * "vencida" é derivada (`vencida` no payload) e ganha Pill danger.
 * Criação/edição em SlideOverPanel; "Tornar firme" (cotacao:aprovar) em
 * painel curto com valor_cotado + validade obrigatórios — também serve de
 * "Renovar" numa firme vencida/perto de vencer.
 *
 * O preview do multiplicador efetivo usa `fator_lucro ÷ divisor` — o divisor
 * vem por prop ou de um GET /preco silencioso (melhor esforço).
 *
 * Três escopos (backend expõe `escopo` = OBRA | AMBIENTE | MOVEL):
 *
 *   - `itemId`   → bloco DENTRO do móvel: lista só as cotações daquele móvel
 *                  e a criação nasce com item + ambiente preenchidos.
 *   - `ambienteId` (sem itemId) → bloco dentro do card do ambiente: lista as
 *                  do ambiente EXCLUINDO as que pertencem a um móvel (essas
 *                  aparecem no card do próprio móvel).
 *   - nenhum dos dois → visão agregada do orçamento (obra + ambiente + móvel),
 *                  com a coluna "Escopo" dizendo onde cada uma está pendurada.
 *
 * `cotacoesExternas` deixa o pai ser dono da lista (um GET só para a tela
 * inteira em vez de um por bloco); toda mutação dispara
 * "orc2:preco-invalidado", que é o gatilho de recarga do pai.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  FiCheckCircle, FiEdit2, FiPlus, FiRefreshCw, FiTrash2,
} from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import { MiniSpinner } from "../../../components/LoadingMarcenaria";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import {
  FATOR_LUCRO_PADRAO_POR_TIPO,
  SITUACAO_LABEL_COTACAO,
  SITUACAO_TONE_COTACAO,
  TIPOS_COTACAO,
  atualizarCotacao,
  criarCotacao,
  excluirCotacao,
  listarCotacoes,
  tornarCotacaoFirme,
} from "../../../services/cotacoes";
import { getPrecoOrcamento } from "../../../services/motorPreco";

import { fmtData, fmtMoeda, fmtNumero } from "../helpers";
import {
  EmptyMsg,
  Field,
  FormGrid,
  IconButton,
  InlineHint,
  Input,
  Pill,
  SecondaryButton,
  SectionTitle,
  Select,
  Table,
  TableWrapper,
  Textarea,
  WarningMsg,
  AcoesCotacao,
} from "../styles";

const VALIDADE_PADRAO_DIAS = 45; // Q7
const AVISO_VENCIMENTO_DIAS = 7;

function normalizarLista(payload) {
  if (Array.isArray(payload)) return payload;
  return payload?.results || [];
}

function dataMaisDias(dias) {
  const d = new Date();
  d.setDate(d.getDate() + dias);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function diasAteValidade(validadeAte) {
  if (!validadeAte) return null;
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const alvo = new Date(`${validadeAte}T00:00:00`);
  return Math.round((alvo - hoje) / (24 * 60 * 60 * 1000));
}

function tipoLabel(codigo) {
  const t = TIPOS_COTACAO.find((x) => x.codigo === codigo);
  return t ? t.label : codigo;
}

const FORM_VAZIO = {
  tipo: "OUTROS",
  fornecedor_nome: "",
  ambiente: "",
  item: "",
  descricao_escopo: "",
  valor_estimado: "",
  fator_lucro: FATOR_LUCRO_PADRAO_POR_TIPO.OUTROS,
  validade_ate: "",
};

// Escopo derivado do payload (o backend expõe `escopo`; o fallback cobre um
// payload antigo em cache). Sem cor por escopo — cor só comunica estado.
function escopoDe(cotacao) {
  if (cotacao.escopo) return cotacao.escopo;
  if (cotacao.item) return "MOVEL";
  if (cotacao.ambiente) return "AMBIENTE";
  return "OBRA";
}

export default function CotacoesBloco({
  orcamento,
  ambientes = [],
  ambienteId = null,
  itemId = null,
  // Lista já carregada pelo pai (AmbientesV2 carrega uma vez para a tela toda).
  // Quando informada, este bloco não faz GET próprio.
  cotacoesExternas = null,
  editavel = true,
  divisor = null,
  fatoresPadrao = null,
  onChange,
}) {
  const { request, hasAllPermissions, isMaster } = useAuth();

  const listaExterna = Array.isArray(cotacoesExternas);
  const [cotacoesLocais, setCotacoesLocais] = useState([]);
  const [carregando, setCarregando] = useState(!listaExterna);
  const [divisorLocal, setDivisorLocal] = useState(null);
  // panel: null | {modo: "criar"} | {modo: "editar", cotacao} | {modo: "firmar", cotacao}
  const [panel, setPanel] = useState(null);
  const [form, setForm] = useState(FORM_VAZIO);
  const [firmar, setFirmar] = useState({ valor_cotado: "", validade_ate: "" });

  const podeAprovar = isMaster || hasAllPermissions(["cotacao:aprovar"]);

  const carregar = useCallback(async () => {
    // Pai é dono da lista: recarrega no evento "orc2:preco-invalidado".
    if (listaExterna) return;
    setCarregando(true);
    try {
      const payload = await listarCotacoes(
        request,
        { orcamento: orcamento.id },
        { silentError: true },
      );
      setCotacoesLocais(normalizarLista(payload));
    } catch {
      setCotacoesLocais([]);
    } finally {
      setCarregando(false);
    }
  }, [request, orcamento.id, listaExterna]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Recarga silenciosa: uma cotação criada em OUTRO bloco da mesma tela (no
  // móvel, por exemplo) muda esta lista. Quem é dono da própria lista relista
  // no mesmo evento que o resto do orc2 escuta; quem recebe a lista pronta
  // depende do pai, que já escuta.
  useEffect(() => {
    if (listaExterna) return undefined;
    const handler = () => carregar();
    window.addEventListener("orc2:preco-invalidado", handler);
    return () => window.removeEventListener("orc2:preco-invalidado", handler);
  }, [carregar, listaExterna]);

  const cotacoes = listaExterna ? cotacoesExternas : cotacoesLocais;

  useEffect(() => {
    if (divisor !== null || orcamento?.motor_preco !== "DIVISOR") {
      return undefined;
    }
    let ativo = true;
    (async () => {
      try {
        const payload = await getPrecoOrcamento(request, orcamento.id, {
          silentError: true,
        });
        if (ativo) setDivisorLocal(Number(payload?.divisor) || null);
      } catch {
        if (ativo) setDivisorLocal(null);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [request, orcamento?.id, orcamento?.motor_preco, divisor]);

  const divisorEfetivo = divisor !== null ? Number(divisor) : divisorLocal;

  const aposMutacao = async () => {
    setPanel(null);
    window.dispatchEvent(new CustomEvent("orc2:preco-invalidado"));
    await carregar();
    if (onChange) await onChange();
  };

  const abrirCriar = () => {
    setForm({
      ...FORM_VAZIO,
      // Modo por-ambiente/por-móvel: nasce com o escopo do card já preenchido.
      ambiente: ambienteId || "",
      item: itemId || "",
      validade_ate: dataMaisDias(VALIDADE_PADRAO_DIAS),
    });
    setPanel({ modo: "criar" });
  };

  const abrirEditar = (cotacao) => {
    setForm({
      tipo: cotacao.tipo,
      fornecedor_nome: cotacao.fornecedor_nome || "",
      ambiente: cotacao.ambiente || "",
      item: cotacao.item || "",
      descricao_escopo: cotacao.descricao_escopo || "",
      valor_estimado: String(cotacao.valor_estimado ?? ""),
      fator_lucro: String(cotacao.fator_lucro ?? "2.000"),
      validade_ate: cotacao.validade_ate || "",
    });
    setPanel({ modo: "editar", cotacao });
  };

  const abrirFirmar = (cotacao) => {
    setFirmar({
      valor_cotado: String(
        cotacao.valor_cotado ?? cotacao.valor_estimado ?? "",
      ),
      validade_ate: dataMaisDias(VALIDADE_PADRAO_DIAS),
    });
    setPanel({ modo: "firmar", cotacao });
  };

  const mudarTipo = (tipo) => {
    const padrao = (fatoresPadrao && fatoresPadrao[tipo])
      || FATOR_LUCRO_PADRAO_POR_TIPO[tipo]
      || "1.500";
    setForm((prev) => ({ ...prev, tipo, fator_lucro: String(padrao) }));
  };

  const salvarForm = async () => {
    const dados = {
      tipo: form.tipo,
      fornecedor_nome: form.fornecedor_nome,
      ambiente: form.ambiente || null,
      // Escopo MOVEL: o backend deriva/grava o ambiente do próprio móvel.
      item: form.item || null,
      descricao_escopo: form.descricao_escopo,
      valor_estimado: String(form.valor_estimado).replace(",", "."),
      fator_lucro: String(form.fator_lucro).replace(",", "."),
      validade_ate: form.validade_ate || null,
    };
    if (panel?.modo === "criar") {
      await criarCotacao(request, { orcamento: orcamento.id, ...dados });
    } else {
      await atualizarCotacao(request, panel.cotacao.id, dados);
    }
    await aposMutacao();
  };

  const confirmarFirme = async () => {
    await tornarCotacaoFirme(request, panel.cotacao.id, {
      valor_cotado: String(firmar.valor_cotado).replace(",", "."),
      validade_ate: firmar.validade_ate,
    });
    await aposMutacao();
  };

  const excluir = async (cotacao) => {
    await excluirCotacao(request, cotacao.id);
    // Excluir mexe no numerador tanto quanto criar — o preço tem de ser
    // reconsultado (e é o gatilho de recarga do pai dono da lista).
    await aposMutacao();
  };

  // Dentro do móvel o card já carrega dois blocos — a tabela vai enxuta
  // (sem Nº/Escopo/Validade, que vão para o tooltip) para caber sem scroll.
  const compacto = Boolean(itemId);

  const cotacoesVisiveis = itemId
    // Bloco dentro do móvel: só as cotações penduradas NESTE móvel.
    ? cotacoes.filter((c) => String(c.item || "") === String(itemId))
    : ambienteId
      // Bloco dentro do ambiente: as do ambiente que NÃO pertencem a um móvel.
      // Sem esse `!c.item` a mesma cotação apareceria duas vezes na tela (aqui
      // e dentro do card do móvel), porque o backend grava o ambiente derivado
      // do móvel junto com o item.
      ? cotacoes.filter(
          (c) => String(c.ambiente || "") === String(ambienteId) && !c.item,
        )
      // Visão agregada do orçamento: tudo, com a coluna "Escopo" localizando.
      : cotacoes;

  // Nome do ambiente/móvel resolvido da árvore que a tela já tem em memória
  // (o serializer de LISTA não traz `ambiente_nome`/`item_descricao`).
  const nomeAmbiente = (id) => {
    const a = ambientes.find((x) => String(x.id) === String(id));
    return a ? a.nome : null;
  };
  const nomeMovel = (id) => {
    for (const a of ambientes) {
      const it = (a.itens || []).find((x) => String(x.id) === String(id));
      if (it) return it.descricao;
    }
    return null;
  };
  const rotuloEscopo = (c) => {
    const escopo = escopoDe(c);
    if (escopo === "MOVEL") {
      const movel = nomeMovel(c.item) || c.item_descricao || "móvel";
      const amb = nomeAmbiente(c.ambiente) || c.ambiente_nome;
      return {
        curto: `Móvel · ${movel}`,
        completo: amb ? `Móvel "${movel}" (${amb})` : `Móvel "${movel}"`,
      };
    }
    if (escopo === "AMBIENTE") {
      const amb = nomeAmbiente(c.ambiente) || c.ambiente_nome || "ambiente";
      return { curto: amb, completo: `Ambiente "${amb}"` };
    }
    return { curto: "Obra inteira", completo: "Obra inteira" };
  };

  const somaValor = cotacoesVisiveis.reduce(
    (acc, c) => acc + Number(c.valor_efetivo || 0),
    0,
  );
  const somaCarregado = cotacoesVisiveis.reduce(
    (acc, c) =>
      acc + Number(c.valor_efetivo || 0) * Number(c.fator_lucro || 1),
    0,
  );

  const fatorForm = Number(String(form.fator_lucro).replace(",", "."));
  const multiplicadorPreview = divisorEfetivo && Number.isFinite(fatorForm)
    ? fatorForm / divisorEfetivo
    : null;
  const formValido = Boolean(form.tipo)
    && Number(String(form.valor_estimado).replace(",", ".")) >= 0
    && Number.isFinite(fatorForm)
    && fatorForm >= 1
    && fatorForm <= 10;
  const firmarValido = Number(String(firmar.valor_cotado).replace(",", ".")) > 0
    && Boolean(firmar.validade_ate);

  // Aviso do rebaixamento: já existe FIRME do mesmo (tipo, ambiente, item)?
  // A chave inclui o móvel — dois móveis do mesmo ambiente com a mesma
  // laqueação não se derrubam (mesma regra do service).
  const firmeConcorrente = panel?.modo === "firmar"
    ? cotacoes.find(
        (c) =>
          c.id !== panel.cotacao.id
          && c.situacao === "FIRME"
          && c.tipo === panel.cotacao.tipo
          && String(c.ambiente || "") === String(panel.cotacao.ambiente || "")
          && String(c.item || "") === String(panel.cotacao.item || ""),
      )
    : null;

  return (
    <section>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <SectionTitle>
          {itemId
            ? "Terceiros e serviços do móvel"
            : ambienteId
              ? "Terceiros e serviços do ambiente"
              : "Terceiros e serviços"}
        </SectionTitle>
        {editavel && (
          <SecondaryButton type="button" onClick={abrirCriar}>
            <FiPlus size={12} /> Nova cotação
          </SecondaryButton>
        )}
      </div>

      {carregando ? (
        <MiniSpinner label="Carregando cotações" />
      ) : cotacoesVisiveis.length === 0 ? (
        <EmptyMsg>
          {itemId
            ? "Nenhum terceiro/serviço neste móvel. Laqueação, vidro e mármore de um móvel específico entram aqui, com fator próprio."
            : ambienteId
              ? "Nenhum terceiro/serviço neste ambiente. Laqueação, vidro, mármore, içamento e frete entram aqui com fator próprio."
              : "Nenhuma cotação de terceiro. Laqueação, vidro, mármore, içamento e frete entram aqui com fator próprio."}
        </EmptyMsg>
      ) : (
        <TableWrapper>
          <Table>
            <colgroup>
              {!compacto && <col style={{ width: "12%" }} />}
              <col style={{ width: compacto ? "16%" : "11%" }} />
              <col />
              {!compacto && <col style={{ width: "11%" }} />}
              <col style={{ width: compacto ? "14%" : "10%" }} />
              <col style={{ width: compacto ? "9%" : "8%" }} />
              <col style={{ width: compacto ? "14%" : "11%" }} />
              {!compacto && <col style={{ width: "10%" }} />}
              <col style={{ width: compacto ? "13%" : "10%" }} />
              {editavel && <col style={{ width: compacto ? 96 : 120 }} />}
            </colgroup>
            <thead>
              <tr>
                {!compacto && <th>Nº</th>}
                <th>Tipo</th>
                <th>Fornecedor</th>
                {!compacto && <th>Escopo</th>}
                <th>Valor</th>
                <th>Fator</th>
                <th>Carregado</th>
                {!compacto && <th>Validade</th>}
                <th>Situação</th>
                {editavel && <th aria-label="Ações" />}
              </tr>
            </thead>
            <tbody>
              {cotacoesVisiveis.map((c) => {
                const dias = diasAteValidade(c.validade_ate);
                const perto = c.situacao === "FIRME"
                  && !c.vencida
                  && dias !== null
                  && dias <= AVISO_VENCIMENTO_DIAS;
                const carregado =
                  Number(c.valor_efetivo || 0) * Number(c.fator_lucro || 1);
                const multiplicador = divisorEfetivo
                  ? Number(c.fator_lucro || 1) / divisorEfetivo
                  : null;
                const escopo = rotuloEscopo(c);
                const tipoTexto = c.tipo_display || tipoLabel(c.tipo);
                return (
                  <tr key={c.id}>
                    {!compacto && <td title={c.numero}>{c.numero}</td>}
                    <td
                      title={
                        compacto
                          // No compacto o Nº e a validade vivem no tooltip.
                          ? `${c.numero} — ${tipoTexto}`
                            + `${c.validade_ate ? ` · validade ${fmtData(c.validade_ate)}` : ""}`
                          : tipoTexto
                      }
                    >
                      {tipoTexto}
                    </td>
                    <td title={c.parceiro_nome || c.fornecedor_nome || "ainda não definido"}>
                      {c.parceiro_nome || c.fornecedor_nome || "—"}
                    </td>
                    {!compacto && (
                      <td title={escopo.completo}>
                        <Pill $tone="neutral">{escopo.curto}</Pill>
                      </td>
                    )}
                    <td>{fmtMoeda(c.valor_efetivo)}</td>
                    <td
                      title={
                        multiplicador
                          ? `×${fmtNumero(c.fator_lucro, 3)} ÷ divisor = efetivo ×${fmtNumero(multiplicador, 4)}`
                          : "fator_lucro (aplicado antes da divisão)"
                      }
                    >
                      ×{fmtNumero(c.fator_lucro, 3)}
                    </td>
                    <td>{fmtMoeda(carregado)}</td>
                    {!compacto && <td>{fmtData(c.validade_ate)}</td>}
                    <td>
                      {c.vencida ? (
                        <Pill $tone="danger">
                          vencida{dias !== null ? ` há ${Math.abs(dias)} d` : ""}
                        </Pill>
                      ) : perto ? (
                        <Pill $tone="warning">vence em {dias} d</Pill>
                      ) : (
                        <Pill $tone={SITUACAO_TONE_COTACAO[c.situacao] || "neutral"}>
                          {SITUACAO_LABEL_COTACAO[c.situacao] || c.situacao}
                        </Pill>
                      )}
                    </td>
                    {editavel && (
                      /* Reteste 2026-08-18: três botões com rótulo de texto
                         somam ~210px dentro de uma coluna de 96px — a ação
                         "Tornar firme" ficava truncada em "Iar …" e não
                         clicava, deixando o vendedor sem como destravar a
                         aprovação do cliente. Os rótulos viraram `title`,
                         que é onde eles cabem; nenhuma informação se perde. */
                      <td>
                        <AcoesCotacao>
                          <IconButton
                            type="button"
                            onClick={() => abrirEditar(c)}
                            title="Editar cotação"
                            aria-label="Editar cotação"
                          >
                            <FiEdit2 size={13} aria-hidden="true" />
                          </IconButton>
                          {c.situacao === "ESTIMADA" && podeAprovar && (
                            <IconButton
                              type="button"
                              onClick={() => abrirFirmar(c)}
                              title="Registrar o valor firme do fornecedor"
                              aria-label="Tornar firme"
                            >
                              <FiCheckCircle size={13} aria-hidden="true" />
                            </IconButton>
                          )}
                          {c.situacao === "FIRME" && podeAprovar
                            && (c.vencida || perto) && (
                            <IconButton
                              type="button"
                              onClick={() => abrirFirmar(c)}
                              title="Renovar valor e validade"
                              aria-label="Renovar cotação"
                            >
                              <FiRefreshCw size={13} aria-hidden="true" />
                            </IconButton>
                          )}
                          {c.situacao === "ESTIMADA" && (
                            <AsyncButton
                              variant="icon"
                              onClick={() => excluir(c)}
                              successMessage="Cotação excluída."
                              title="Excluir (só estimada)"
                              aria-label="Excluir cotação"
                            >
                              <FiTrash2 size={13} aria-hidden="true" />
                            </AsyncButton>
                          )}
                        </AcoesCotacao>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <th colSpan={compacto ? 2 : 4}>Σ</th>
                <th>{fmtMoeda(somaValor)}</th>
                <th />
                <th>{fmtMoeda(somaCarregado)}</th>
                <th colSpan={(compacto ? 1 : 2) + (editavel ? 1 : 0)} />
              </tr>
            </tfoot>
          </Table>
        </TableWrapper>
      )}

      {/* Painel de criação/edição */}
      <SlideOverPanel
        open={panel?.modo === "criar" || panel?.modo === "editar"}
        onClose={() => setPanel(null)}
        title={panel?.modo === "criar" ? "Nova cotação" : `Editar ${panel?.cotacao?.numero || ""}`}
        subtitle="Serviço de terceiro que entra no custo com fator próprio"
        width="min(95vw, 560px)"
        footer={
          <AsyncButton
            variant="primary"
            onClick={salvarForm}
            disabled={!formValido}
            successMessage="Cotação salva."
          >
            {panel?.modo === "criar" ? "Criar cotação" : "Salvar"}
          </AsyncButton>
        }
      >
        <FormGrid>
          <Field>
            Tipo
            <Select
              value={form.tipo}
              onChange={(e) => mudarTipo(e.target.value)}
            >
              {TIPOS_COTACAO.map((t) => (
                <option key={t.codigo} value={t.codigo}>
                  {t.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field>
            Fornecedor
            <Input
              value={form.fornecedor_nome}
              onChange={(e) =>
                setForm((p) => ({ ...p, fornecedor_nome: e.target.value }))
              }
              placeholder="ainda não definido"
            />
          </Field>
          <Field>
            Escopo
            {form.item ? (
              // Cotação de móvel: o escopo é o próprio móvel do card. Trocar o
              // ambiente aqui deixaria o vínculo incoerente (o backend recusa).
              <InlineHint
                title="Para pendurar em outro lugar, crie a cotação no card do ambiente ou da obra."
              >
                {`Móvel · ${nomeMovel(form.item) || "este móvel"}`}
              </InlineHint>
            ) : (
              <Select
                value={form.ambiente}
                onChange={(e) =>
                  setForm((p) => ({ ...p, ambiente: e.target.value }))
                }
              >
                <option value="">Obra inteira</option>
                {ambientes.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.nome}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field>
            Validade
            <Input
              type="date"
              value={form.validade_ate}
              onChange={(e) =>
                setForm((p) => ({ ...p, validade_ate: e.target.value }))
              }
            />
          </Field>
          <Field>
            Valor estimado (R$)
            <Input
              inputMode="decimal"
              value={form.valor_estimado}
              onChange={(e) =>
                setForm((p) => ({ ...p, valor_estimado: e.target.value }))
              }
              placeholder="0,00"
            />
          </Field>
          <Field>
            Fator de lucro (1,000 a 10,000)
            <Input
              inputMode="decimal"
              value={form.fator_lucro}
              onChange={(e) =>
                setForm((p) => ({ ...p, fator_lucro: e.target.value }))
              }
            />
            <InlineHint
              title={
                divisorEfetivo
                  ? `fator_lucro ÷ divisor = ${fmtNumero(fatorForm, 3)} ÷ ${fmtNumero(divisorEfetivo, 4)}`
                  : "O multiplicador efetivo aparece quando o preço do orçamento carregar."
              }
            >
              {multiplicadorPreview
                ? `×${fmtNumero(fatorForm, 3)} → efetivo ×${fmtNumero(multiplicadorPreview, 4)} na proposta`
                : "efetivo = fator ÷ divisor"}
            </InlineHint>
          </Field>
          <Field className="full">
            O que foi pedido ao fornecedor
            <Textarea
              value={form.descricao_escopo}
              onChange={(e) =>
                setForm((p) => ({ ...p, descricao_escopo: e.target.value }))
              }
            />
          </Field>
        </FormGrid>
        <InlineHint>
          A cotação nasce como estimativa e o valor já entra no custo — piso
          que ignora estimativa mente para baixo. Torne firme quando o
          fornecedor responder.
        </InlineHint>
      </SlideOverPanel>

      {/* Painel curto de tornar firme / renovar */}
      <SlideOverPanel
        open={panel?.modo === "firmar"}
        onClose={() => setPanel(null)}
        title={`Tornar firme — ${panel?.cotacao?.numero || ""}`}
        subtitle="Valor respondido pelo fornecedor + nova validade"
        width="min(95vw, 440px)"
        footer={
          <AsyncButton
            variant="primary"
            onClick={confirmarFirme}
            disabled={!firmarValido}
            successMessage="Cotação firme."
          >
            Confirmar valor firme
          </AsyncButton>
        }
      >
        {firmeConcorrente && (
          <WarningMsg>
            {`Já existe uma cotação firme de ${tipoLabel(panel.cotacao.tipo)} em `}
            {`${rotuloEscopo(panel.cotacao).completo} `}
            {`(${firmeConcorrente.numero}, ${fmtMoeda(firmeConcorrente.valor_efetivo)}). `}
            Tornar esta firme rebaixa aquela para estimativa.
          </WarningMsg>
        )}
        <FormGrid>
          <Field>
            Valor cotado (R$)
            <Input
              inputMode="decimal"
              value={firmar.valor_cotado}
              onChange={(e) =>
                setFirmar((p) => ({ ...p, valor_cotado: e.target.value }))
              }
              placeholder="0,00"
            />
          </Field>
          <Field>
            Validade
            <Input
              type="date"
              value={firmar.validade_ate}
              onChange={(e) =>
                setFirmar((p) => ({ ...p, validade_ate: e.target.value }))
              }
            />
          </Field>
        </FormGrid>
      </SlideOverPanel>
    </section>
  );
}
